/* wos-vehicle-fitment.js
   Universal component: wos-vehicle-fitment
   Token format stored in metafield: YEAR|MAKE|MODEL|STARTTYPE|DRIVETRAIN|TRIM
   NOTE: Some stores currently use 4-part tokens (YEAR|MAKE|MODEL|STARTTYPE). We must filter using the ORIGINAL token string.
*/

(() => {
  const ROOT_SELECTOR = "[data-wos-vehicle-fitment]";
  const TOKEN_FIELDS = ["year", "make", "model", "starttype", "drivetrain", "trim"];
  const FIELD_IDX = { year: 0, make: 1, model: 2, starttype: 3, drivetrain: 4, trim: 5 };

  const FITMENT_PREFIX = "filter.p.m.custom.vf_fitment_index_";

  function qs(root, sel) { return root.querySelector(sel); }

  function normalize(s) { return (s ?? "").toString().trim(); }

  // Always pad to 6 for UI matching, but never use padded parts to build the filter token string.
  function parseParts(tokenStr) {
    const parts = tokenStr.split("|").map(normalize);
    while (parts.length < 6) parts.push("");
    return parts.slice(0, 6);
  }

  function wipeOtherFitmentParams(url, keepParam) {
    const kill = [];
    for (const [k] of url.searchParams.entries()) {
      if (k.startsWith(FITMENT_PREFIX) && k !== keepParam) kill.push(k);
    }
    kill.forEach(k => url.searchParams.delete(k));
  }

  function buildUrlWithTokens(filterParam, tokens) {
    const url = new URL(window.location.href);
    url.searchParams.delete("page");            // reset pagination
    wipeOtherFitmentParams(url, filterParam);   // remove any old vf params
    url.searchParams.delete(filterParam);       // replace previous selection

    tokens.forEach(t => url.searchParams.append(filterParam, t));
    return url.toString();
  }

  function getExistingTokensFromUrl(filterParam) {
    const url = new URL(window.location.href);
    return url.searchParams.getAll(filterParam) || [];
  }

  function hideGrid() { document.documentElement.classList.add("wosvf-hide-grid"); }
  function showGrid() { document.documentElement.classList.remove("wosvf-hide-grid"); }

  async function fetchIndexAllPages(indexUrl, statusEl) {
    const all = [];
    const u = new URL(indexUrl, window.location.origin);

    async function fetchPage(p) {
      u.searchParams.set("page", String(p));
      const res = await fetch(u.toString(), { credentials: "same-origin" });
      if (!res.ok) throw new Error(`Index fetch failed (page ${p})`);
      return res.json();
    }

    if (statusEl) statusEl.textContent = "Loading fitment index…";
    const first = await fetchPage(1);
    if (Array.isArray(first.tokens)) all.push(...first.tokens);

    const pages = Number(first.pages || 1);
    for (let p = 2; p <= pages; p++) {
      if (statusEl) statusEl.textContent = `Loading fitment index… (${p}/${pages})`;
      const data = await fetchPage(p);
      if (Array.isArray(data.tokens)) all.push(...data.tokens);
    }

    if (statusEl) statusEl.textContent = "";
    return all;
  }

  function uniqueSorted(values, sortMode = "alpha") {
    const out = Array.from(new Set(values.filter(v => normalize(v) !== "")));
    if (sortMode === "year") out.sort((a, b) => Number(a) - Number(b));
    else out.sort((a, b) => a.localeCompare(b));
    return out;
  }

  function optionize(selectEl, values, placeholderText) {
    selectEl.innerHTML = "";
    const ph = document.createElement("option");
    ph.value = "";
    ph.textContent = placeholderText;
    selectEl.appendChild(ph);
    values.forEach(v => {
      const opt = document.createElement("option");
      opt.value = v;
      opt.textContent = v;
      selectEl.appendChild(opt);
    });
  }

  function setDisabled(el, disabled) {
    el.disabled = !!disabled;
    el.classList.toggle("is-disabled", !!disabled);
  }

  function resetDownstream(selectMap, fromKey) {
    const order = ["year", "make", "model", "starttype", "drivetrain", "trim"];
    const start = order.indexOf(fromKey);
    for (let i = start + 1; i < order.length; i++) {
      const k = order[i];
      selectMap[k].value = "";
      setDisabled(selectMap[k], true);
    }
  }

  function matchesCriteria(parts, criteria) {
    for (const key of TOKEN_FIELDS) {
      const v = criteria[key];
      if (!v) continue;
      if (parts[FIELD_IDX[key]] !== v) return false;
    }
    return true;
  }

  function nextOptions(rows, criteria, fieldKey) {
    const idx = FIELD_IDX[fieldKey];
    const filtered = rows.filter(r => matchesCriteria(r.parts, criteria));
    return uniqueSorted(filtered.map(r => r.parts[idx]), fieldKey === "year" ? "year" : "alpha");
  }

  function tokensForSearch(rows, criteria) {
    // Required: year/make/model/starttype
    if (!criteria.year || !criteria.make || !criteria.model || !criteria.starttype) return [];

    // First match required 4
    const base = {
      year: criteria.year,
      make: criteria.make,
      model: criteria.model,
      starttype: criteria.starttype
    };

    let filtered = rows.filter(r => matchesCriteria(r.parts, base));

    // Optional constraints if selected
    if (criteria.drivetrain) filtered = filtered.filter(r => r.parts[FIELD_IDX.drivetrain] === criteria.drivetrain);
    if (criteria.trim) filtered = filtered.filter(r => r.parts[FIELD_IDX.trim] === criteria.trim);

    // IMPORTANT: return ORIGINAL token strings exactly
    return Array.from(new Set(filtered.map(r => r.token)));
  }

  function initOne(root) {
    const indexUrl = root.getAttribute("data-index-url");
    const filterParam = root.getAttribute("data-filter-param");
    const hideGridUntil = root.getAttribute("data-hide-grid") === "true";

    const statusEl = qs(root, "[data-wosvf-status]");
    const searchBtn = qs(root, "[data-wosvf-action='search']");
    const resetBtn  = qs(root, "[data-wosvf-action='reset']");

    const selects = {
      year: qs(root, "[data-wosvf='year']"),
      make: qs(root, "[data-wosvf='make']"),
      model: qs(root, "[data-wosvf='model']"),
      starttype: qs(root, "[data-wosvf='starttype']"),
      drivetrain: qs(root, "[data-wosvf='drivetrain']"),
      trim: qs(root, "[data-wosvf='trim']")
    };

    if (hideGridUntil) {
      const existing = getExistingTokensFromUrl(filterParam);
      if (!existing.length) hideGrid();
      else showGrid();
    }

    function criteriaFromSelects() {
      return {
        year: selects.year.value,
        make: selects.make.value,
        model: selects.model.value,
        starttype: selects.starttype.value,
        drivetrain: selects.drivetrain.value,
        trim: selects.trim.value
      };
    }

    function updateSearchEnabled() {
      const ok = !!selects.year.value && !!selects.make.value && !!selects.model.value && !!selects.starttype.value;
      searchBtn.disabled = !ok;
    }

    fetchIndexAllPages(indexUrl, statusEl).then(tokens => {
      // rows = [{token: originalString, parts: [paddedParts]}]
      const rows = [];
      const seen = new Set();

      for (const t of tokens) {
        const tok = normalize(t);
        if (!tok) continue;
        if (seen.has(tok)) continue;
        seen.add(tok);
        rows.push({ token: tok, parts: parseParts(tok) });
      }

      // Populate Year
      optionize(selects.year, uniqueSorted(rows.map(r => r.parts[0]), "year"), "Select year");
      setDisabled(selects.year, false);

      selects.year.addEventListener("change", () => {
        resetDownstream(selects, "year");
        if (!selects.year.value) return updateSearchEnabled();

        optionize(selects.make, nextOptions(rows, { year: selects.year.value }, "make"), "Select make");
        setDisabled(selects.make, false);
        updateSearchEnabled();
      });

      selects.make.addEventListener("change", () => {
        resetDownstream(selects, "make");
        const c = criteriaFromSelects();
        if (!c.make) return updateSearchEnabled();

        optionize(selects.model, nextOptions(rows, { year: c.year, make: c.make }, "model"), "Select model");
        setDisabled(selects.model, false);
        updateSearchEnabled();
      });

      selects.model.addEventListener("change", () => {
        resetDownstream(selects, "model");
        const c = criteriaFromSelects();
        if (!c.model) return updateSearchEnabled();

        optionize(selects.starttype, nextOptions(rows, { year: c.year, make: c.make, model: c.model }, "starttype"), "Select start type");
        setDisabled(selects.starttype, false);
        updateSearchEnabled();
      });

      selects.starttype.addEventListener("change", () => {
        resetDownstream(selects, "starttype");
        const c = criteriaFromSelects();
        if (!c.starttype) return updateSearchEnabled();

        const base = { year: c.year, make: c.make, model: c.model, starttype: c.starttype };

        optionize(selects.drivetrain, nextOptions(rows, base, "drivetrain"), "Any drivetrain");
        setDisabled(selects.drivetrain, false);

        optionize(selects.trim, nextOptions(rows, base, "trim"), "Any trim");
        setDisabled(selects.trim, false);

        updateSearchEnabled();
      });

      selects.drivetrain.addEventListener("change", () => {
        const c = criteriaFromSelects();
        if (!c.starttype) return;

        const base = { year: c.year, make: c.make, model: c.model, starttype: c.starttype };
        if (c.drivetrain) base.drivetrain = c.drivetrain;

        optionize(selects.trim, nextOptions(rows, base, "trim"), "Any trim");
        selects.trim.value = "";
        updateSearchEnabled();
      });

      selects.trim.addEventListener("change", updateSearchEnabled);

      searchBtn.addEventListener("click", () => {
        const criteria = criteriaFromSelects();
        const orTokens = tokensForSearch(rows, criteria);

        if (!orTokens.length) {
          if (statusEl) statusEl.textContent = "No matches for that selection.";
          return;
        }

        showGrid();
        window.location.href = buildUrlWithTokens(filterParam, orTokens);
      });

      resetBtn.addEventListener("click", () => {
        const url = new URL(window.location.href);
        url.searchParams.delete("page");
        wipeOtherFitmentParams(url, filterParam);
        url.searchParams.delete(filterParam);
        window.location.href = url.toString();
      });

      updateSearchEnabled();
    }).catch(err => {
      console.error(err);
      if (statusEl) statusEl.textContent = "Could not load fitment index.";
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initOne);
  }

  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", (e) => {
    const root = e.target?.querySelector?.(ROOT_SELECTOR);
    if (root) initOne(root);
  });
})();

